import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import { SiteFooter } from '../components/SiteFooter';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { Shield, Target, TrendingUp, Lock, Unlock, ChevronRight, BookOpen, PlayCircle, Loader2, Trophy, Radio, Calculator, Bot, Gift } from 'lucide-react';
import { PendingBanner, UnlockDialog, isLockedStatus, WinTeasePopup } from '../components/UnlockGate';
import { CountdownRing } from '../components/CountdownRing';
import { WelcomeCelebration } from '../components/WelcomeCelebration';
import { ReviewPopup } from '../components/ReviewPopup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_META = {
    safe_start: { icon: Shield, color: '#3B82F6', bgClass: 'badge-phase-1' },
    building_consistency: { icon: Target, color: '#8B5CF6', bgClass: 'badge-phase-2' },
    capital_confidence: { icon: TrendingUp, color: '#D4AF37', bgClass: 'badge-phase-3' }
};

export default function StudentDashboard() {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const [courses, setCourses] = useState([]);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showChallengePrompt, setShowChallengePrompt] = useState(false);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [activeChallenge, setActiveChallenge] = useState(null);
    const [celebrate, setCelebrate] = useState(false);
    const navigate = useNavigate();
    const isPending = isLockedStatus(user);

    useEffect(() => {
        if (!isPending && user?.role === 'student' && user?.welcome_celebrated === false) {
            setCelebrate(true);
        }
    }, [user]);

    useEffect(() => {
        if (isPending) return;
        axios.get(`${API}/challenge/me`, { withCredentials: true })
            .then((r) => {
                const cur = r.data.current;
                if (cur && cur.status === 'active') setActiveChallenge(cur);
                const dismissedAt = parseInt(localStorage.getItem('ptg_challenge_prompt') || '0', 10);
                if (Date.now() - dismissedAt < 3 * 24 * 3600 * 1000) return;
                if (!cur || !['active', 'submitted', 'winner'].includes(cur.status)) setShowChallengePrompt(true);
            })
            .catch(() => {});
    }, []);

    const dismissChallengePrompt = () => {
        localStorage.setItem('ptg_challenge_prompt', String(Date.now()));
        setShowChallengePrompt(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const fetchData = async () => {
        try {
            if (isPending) return;
            const [coursesRes, progressRes] = await Promise.all([
                axios.get(`${API}/courses`, {
                    params: { lang: i18n.language },
                    withCredentials: true
                }),
                axios.get(`${API}/progress`, { withCredentials: true })
            ]);
            setCourses(coursesRes.data);
            setProgress(progressRes.data);
        } catch (e) {
            console.error('Error fetching data:', e);
        } finally {
            setLoading(false);
        }
    };

    const getPhaseProgress = (phaseKey) => {
        if (!progress) return 0;
        const total = progress.total_lessons[phaseKey] || 0;
        const completed = progress.completed_by_phase[phaseKey] || 0;
        if (total === 0) return 0;
        return Math.round((completed / total) * 100);
    };

    const getPhaseStatus = (phaseKey) => {
        const order = { safe_start: 1, building_consistency: 2, capital_confidence: 3 };
        const currentPhaseOrder = order[user?.current_phase] || 1;
        const targetPhaseOrder = order[phaseKey] || 1;
        return currentPhaseOrder >= targetPhaseOrder;
    };

    const getCoursesByPhase = (phaseKey) => {
        return courses.filter(c => c.phase === phaseKey);
    };

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    if (isPending) {
        const lockedTiles = [
            { key: 'courses', Icon: BookOpen },
            { key: 'signals', Icon: Radio },
            { key: 'riskCalc', Icon: Calculator },
            { key: 'assistant', Icon: Bot },
            { key: 'challenge', Icon: Trophy },
        ];
        return (
            <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10" data-testid="locked-dashboard">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                        {t('dashboard.welcome', { name: user?.name })}
                    </h1>
                    <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>{t('locked.bannerText')}</p>

                    <PendingBanner user={user} onUnlock={() => setUnlockOpen(true)} />

                    <div className="rounded-2xl p-5 sm:p-6 mb-8 flex items-start gap-4" style={{ backgroundColor: 'rgba(255,184,0,0.08)', border: '1px solid rgba(184,134,11,0.35)' }} data-testid="welcome-package-card">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 gold-glow" style={{ backgroundColor: 'var(--primary-color)' }}>
                            <Gift className="w-5 h-5" style={{ color: 'var(--primary-text)' }} />
                        </div>
                        <div>
                            <p className="font-bold text-sm sm:text-base mb-1" style={{ color: 'var(--text-primary)' }}>{t('locked.welcomeTitle')}</p>
                            <p className="text-xs sm:text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{t('locked.welcomeText')}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {lockedTiles.map(({ key, Icon }) => (
                            <button
                                key={key}
                                onClick={() => setUnlockOpen(true)}
                                className="card-surface p-6 text-left relative group hover:border-[var(--border-highlight)] transition-all duration-200"
                                data-testid={`locked-tile-${key}`}
                            >
                                <div className="absolute top-4 end-4 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(184,134,11,0.12)', border: '1px solid rgba(184,134,11,0.35)' }}>
                                    <Lock className="w-3.5 h-3.5" style={{ color: 'var(--primary-color)' }} />
                                </div>
                                <Icon className="w-8 h-8 mb-4" style={{ color: 'var(--text-muted)' }} />
                                <p className="font-bold text-sm sm:text-base mb-1" style={{ color: 'var(--text-primary)' }}>{t(`locked.features.${key}`)}</p>
                                <p className="text-xs font-semibold group-hover:underline" style={{ color: 'var(--primary-color)' }}>{t('locked.tileHint')} →</p>
                            </button>
                        ))}
                        <button
                            onClick={() => setUnlockOpen(true)}
                            className="p-6 rounded-xl text-left flex flex-col items-start justify-center transition-transform duration-200 hover:scale-[1.02]"
                            style={{ background: 'linear-gradient(120deg, #FFB800 0%, #D4A017 100%)' }}
                            data-testid="locked-tile-unlock-cta"
                        >
                            <Unlock className="w-8 h-8 mb-3" style={{ color: '#050505' }} />
                            <p className="font-black text-base" style={{ color: '#050505', fontFamily: "'Outfit', sans-serif" }}>{t('locked.unlockCta')}</p>
                            <p className="text-xs font-semibold" style={{ color: 'rgba(5,5,5,0.7)' }}>{t('locked.dialogIntro')}</p>
                        </button>
                    </div>
                </main>
                <SiteFooter compact />
                <UnlockDialog open={unlockOpen} onOpenChange={setUnlockOpen} />
                <WinTeasePopup onUnlock={() => setUnlockOpen(true)} />
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="dashboard-title">
                        {t('dashboard.welcome', { name: user?.name })}
                    </h1>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {t('dashboard.subtitle')}
                    </p>
                </div>

                {isPending && <PendingBanner user={user} onUnlock={() => setUnlockOpen(true)} />}

                {activeChallenge && (
                    <Link to="/challenge" className="card-surface p-5 mb-8 flex items-center justify-between gap-4 hover:border-[var(--border-highlight)] transition-colors" data-testid="dashboard-challenge-countdown">
                        <div className="flex items-center gap-4">
                            <CountdownRing daysLeft={activeChallenge.days_left} totalDays={31} testId="dashboard-countdown-ring" />
                            <div>
                                <p className="text-xs font-bold tracking-[0.2em] uppercase mb-1" style={{ color: 'var(--primary-color)' }}>
                                    <Trophy className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{t('challenge.navLabel')}
                                </p>
                                <p className="text-lg font-black" style={{ color: 'var(--text-primary)', fontFamily: "'Outfit', sans-serif" }}>
                                    {t('challenge.dayOf', { day: activeChallenge.day })}
                                </p>
                                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                    ${activeChallenge.current_balance} / ${activeChallenge.target} · {activeChallenge.progress_pct}%
                                </p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 shrink-0" style={{ color: 'var(--text-muted)' }} />
                    </Link>
                )}

                {/* Current Phase Card */}
                <div className="card-active p-6 sm:p-8 mb-8" data-testid="current-phase-card">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: 'var(--primary-color)' }}>
                                {t('dashboard.currentPhase')}
                            </p>
                            <h2 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                {t(`phases.${user?.current_phase || 'safe_start'}.name`)}
                            </h2>
                            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                                {t(`phases.${user?.current_phase || 'safe_start'}.weeks`)}
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center gold-glow" style={{ backgroundColor: PHASE_META[user?.current_phase]?.color || '#3B82F6' }}>
                                {(() => {
                                    const Icon = PHASE_META[user?.current_phase]?.icon || Shield;
                                    return <Icon className="w-8 h-8 text-white" />;
                                })()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Phase Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {Object.entries(PHASE_META).map(([key, meta]) => {
                        const isUnlocked = !isPending && getPhaseStatus(key);
                        const Icon = meta.icon;
                        const phaseProgress = getPhaseProgress(key);
                        const phaseCourses = getCoursesByPhase(key);
                        
                        return (
                            <div
                                key={key}
                                className={`phase-card card-surface p-6 ${!isUnlocked ? 'locked' : ''}`}
                                data-testid={`phase-card-${key}`}
                            >
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}20`, border: `1px solid ${meta.color}40` }}>
                                            <Icon className="w-6 h-6" style={{ color: meta.color }} />
                                        </div>
                                        {isUnlocked ? (
                                            <Unlock className="w-5 h-5" style={{ color: 'var(--accent-success)' }} />
                                        ) : (
                                            <Lock className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                                        )}
                                    </div>
                                    
                                    <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: meta.color }}>
                                        {t(`phases.${key}.weeks`)}
                                    </p>
                                    <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                        {t(`phases.${key}.name`)}
                                    </h3>
                                    
                                    <p className="text-sm mb-4 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                                        {t(`phases.${key}.description`)}
                                    </p>
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                                            <span>{phaseCourses.length} {t('dashboard.courses')}</span>
                                            <span>{phaseProgress}% {t('dashboard.complete')}</span>
                                        </div>
                                        <div className="progress-track">
                                            <div
                                                className={`progress-fill ${key === 'safe_start' ? 'phase-1' : key === 'building_consistency' ? 'phase-2' : 'phase-3'}`}
                                                style={{ width: `${phaseProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Courses Section */}
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                            {t('dashboard.yourCourses')}
                        </h2>
                        <Link to="/courses">
                            <Button variant="ghost" className="btn-ghost text-sm" data-testid="view-all-courses-btn">
                                {t('dashboard.viewAll')}
                                <ChevronRight className="w-4 h-4 ml-1 rtl-flip" />
                            </Button>
                        </Link>
                    </div>

                    {courses.length === 0 ? (
                        <div className="card-surface p-12 text-center" data-testid="no-courses-message">
                            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                            <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('dashboard.noCourses')}</p>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('dashboard.noCoursesDesc')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {courses.slice(0, 6).map((course) => {
                                const phaseInfo = PHASE_META[course.phase];
                                const isAccessible = course.accessible;
                                
                                return (
                                    <Link
                                        key={course.id}
                                        to={isAccessible ? `/courses/${course.id}` : '#'}
                                        onClick={(e) => {
                                            if (!isAccessible) {
                                                e.preventDefault();
                                                if (isPending) setUnlockOpen(true);
                                            }
                                        }}
                                        className={`card-surface p-5 block transition-all duration-200 ${isAccessible ? 'hover:border-[var(--border-highlight)]' : isPending ? 'opacity-70 cursor-pointer hover:border-[var(--border-highlight)]' : 'opacity-60 cursor-not-allowed'}`}
                                        data-testid={`course-card-${course.id}`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <span className={`${phaseInfo?.bgClass} px-2 py-1 rounded text-xs font-semibold`}>
                                                {t(`phases.${course.phase}.name`)}
                                            </span>
                                            {!isAccessible && (
                                                <Lock className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                                            )}
                                        </div>
                                        
                                        <h3 className="font-semibold mb-2 line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                                            {course.title}
                                        </h3>
                                        
                                        <p className="text-sm mb-4 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                                            {course.description}
                                        </p>
                                        
                                        <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                                            <span className="flex items-center gap-1">
                                                <PlayCircle className="w-3 h-3" />
                                                {course.lesson_count || 0} {t('dashboard.lessons')}
                                            </span>
                                            {isAccessible && (
                                                <ChevronRight className="w-4 h-4 rtl-flip" style={{ color: 'var(--primary-color)' }} />
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>
            <SiteFooter compact />

            {celebrate && <WelcomeCelebration user={user} onDone={() => setCelebrate(false)} />}
            <ReviewPopup />
            <UnlockDialog open={unlockOpen} onOpenChange={setUnlockOpen} />

            <Dialog open={showChallengePrompt} onOpenChange={(o) => { if (!o) dismissChallengePrompt(); }}>
                <DialogContent className="max-w-md" data-testid="challenge-prompt-dialog">
                    <DialogHeader>
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: 'rgba(255,184,0,0.12)', border: '1px solid rgba(255,184,0,0.4)' }}>
                            <Trophy className="w-7 h-7" style={{ color: '#FFB800' }} />
                        </div>
                        <DialogTitle className="text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {t('challenge.promptTitle')}
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            {t('challenge.promptText')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-2 mt-2">
                        <Button className="btn-primary w-full" onClick={() => { dismissChallengePrompt(); navigate('/challenge'); }} data-testid="challenge-prompt-cta">
                            <Trophy className="w-4 h-4 mr-2" />
                            {t('challenge.promptCta')}
                        </Button>
                        <Button variant="ghost" className="w-full" onClick={dismissChallengePrompt} data-testid="challenge-prompt-dismiss">
                            {t('challenge.promptLater')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
